import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Printer, Package, History, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ today_count: 0, total_count: 0, total_products: 0, most_used_product: "N/A" });
  const [recentLabels, setRecentLabels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, labelsRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/labels?limit=5`),
        ]);
        setStats(statsRes.data);
        setRecentLabels(labelsRes.data);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { label: "LABELS TODAY", value: stats.today_count, icon: Printer, color: "text-primary" },
    { label: "TOTAL LABELS", value: stats.total_count, icon: TrendingUp, color: "text-[hsl(var(--info))]" },
    { label: "PRODUCTS", value: stats.total_products, icon: Package, color: "text-[hsl(var(--success))]" },
    { label: "MOST USED", value: stats.most_used_product, icon: History, color: "text-[hsl(var(--warning))]", isText: true },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
        <div>
          <h1
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            DASHBOARD
          </h1>
          <p className="text-sm text-muted-foreground tracking-wider uppercase mt-1">
            LABEL PRINTING CONTROL CENTER
          </p>
        </div>
        <Button
          data-testid="create-label-hero-btn"
          onClick={() => navigate("/create")}
          className="h-14 px-8 text-lg font-bold uppercase tracking-wider rounded-sm glow-orange bg-primary text-primary-foreground hover:brightness-110 active:scale-95 transition-all duration-75 w-full sm:w-auto"
        >
          <Printer className="w-5 h-5 mr-2" />
          CREATE LABEL
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <Card
            key={stat.label}
            data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s/g, "-")}`}
            className={`bg-card border border-border/50 rounded-sm animate-fade-in-up stagger-${i + 1} corner-brackets`}
          >
            <CardContent className="p-5 relative z-10">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground mb-2">
                    {stat.label}
                  </p>
                  <p className={`${stat.isText ? "text-lg" : "text-3xl"} font-bold font-mono tracking-wide ${stat.color}`}>
                    {loading ? "..." : stat.value}
                  </p>
                </div>
                <stat.icon className={`w-5 h-5 ${stat.color} opacity-40`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Labels */}
      <Card className="bg-card border border-border/50 rounded-sm">
        <div className="p-5 border-b border-border/50 flex items-center justify-between">
          <div>
            <h2
              className="text-xl font-bold tracking-wider uppercase"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              RECENT LABELS
            </h2>
            <p className="text-xs text-muted-foreground tracking-wider mt-0.5">LAST 5 PRINTED</p>
          </div>
          <Button
            data-testid="view-all-history-btn"
            variant="ghost"
            onClick={() => navigate("/history")}
            className="text-xs tracking-wider uppercase hover:text-primary"
          >
            VIEW ALL
          </Button>
        </div>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
          ) : recentLabels.length === 0 ? (
            <div className="p-8 text-center">
              <Printer className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground text-sm">No labels created yet</p>
              <Button
                data-testid="create-first-label-btn"
                onClick={() => navigate("/create")}
                className="mt-4 bg-primary text-primary-foreground rounded-sm"
              >
                CREATE YOUR FIRST LABEL
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border/30">
              {recentLabels.map((label) => (
                <div
                  key={label.id}
                  data-testid={`recent-label-${label.id}`}
                  className="flex items-center justify-between p-4 hover:bg-secondary/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/create?reprint=${label.id}`)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                      <Package className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-bold text-sm uppercase tracking-wide">{label.product_name}</p>
                      <p className="text-xs text-muted-foreground font-mono mt-0.5">
                        {label.weight} {label.weight_unit} | BATCH: {label.batch_number}
                        {label.pack_date ? ` | PKD: ${label.pack_date}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground font-mono">
                      EXP: {label.expiration_date}
                    </p>
                    <p className="text-[10px] text-muted-foreground/50 mt-0.5">
                      {label.created_at ? format(new Date(label.created_at), "dd/MM HH:mm") : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
